import React, { useEffect, useState } from 'react';
import { Fab, Tooltip, Box, Typography, alpha, useTheme, Zoom, Badge } from '@mui/material';
import { ArrowBack, Security, AdminPanelSettings } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const SuperAdminReturnButton = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [portalSwitchData, setPortalSwitchData] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if we came from super admin portal
    const switchData = localStorage.getItem('superAdminPortalSwitch');
    if (switchData) {
      try {
        const data = JSON.parse(switchData);
        // Only show if switch was recent (within last 24 hours)
        const switchTime = new Date(data.timestamp);
        const now = new Date();
        const hoursSinceSwitch = (now - switchTime) / (1000 * 60 * 60);

        if (hoursSinceSwitch < 24 && data.fromSuperAdmin) {
          setPortalSwitchData(data);
          setIsVisible(true);
        }
      } catch (error) {
        console.error('Error parsing portal switch data:', error);
      }
    }
  }, [location]);

  const handleReturn = () => {
    // Clear the portal switch data
    localStorage.removeItem('superAdminPortalSwitch');
    // Navigate back to super admin portal
    navigate(portalSwitchData?.returnPath || '/superadmin/portals');
  };

  if (!isVisible || location.pathname.startsWith('/superadmin')) {
    return null;
  }

  return (
    <Zoom in={isVisible}>
      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 1,
        }}
      >
        {/* Info Badge */}
        <Box
          sx={{
            backgroundColor: alpha(theme.palette.error.main, 0.95),
            color: 'white',
            px: 2,
            py: 1,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`,
            backdropFilter: 'blur(10px)',
            mb: 1,
          }}
        >
          <Security sx={{ fontSize: 18 }} />
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Super Admin Mode Active
          </Typography>
        </Box>

        {/* Return Button */}
        <Tooltip title="Return to Super Admin Portal" placement="left" arrow>
          <Fab
            color="error"
            onClick={handleReturn}
            sx={{
              width: 56,
              height: 56,
              background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
              boxShadow: `0 6px 20px ${alpha(theme.palette.error.main, 0.4)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'scale(1.1)',
                boxShadow: `0 8px 24px ${alpha(theme.palette.error.main, 0.5)}`,
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
            }}
          >
            <Badge
              badgeContent={
                <AdminPanelSettings
                  sx={{
                    fontSize: 14,
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    color: theme.palette.error.main,
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    padding: 0.25,
                  }}
                />
              }
            >
              <ArrowBack />
            </Badge>
          </Fab>
        </Tooltip>
      </Box>
    </Zoom>
  );
};

export default SuperAdminReturnButton;
